import { CheckIcon } from 'lucide-react';
import { ComponentProps, useRef } from 'react';

import { Button } from '@/components/core/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import { cn } from '@/shared/helpers';

import { useWizardContext } from './wizard-context';

type WizardProgressStepState = 'incomplete' | 'current' | 'complete';

function getWizardProgressStepState({
  isComplete,
  isCurrent,
}: {
  isComplete: boolean;
  isCurrent: boolean;
}): WizardProgressStepState {
  if (isComplete) return 'complete';
  if (isCurrent) return 'current';
  return 'incomplete';
}

function WizardProgressStepMarker({
  className,
  index,
  state,
}: {
  className?: string;
  index: number;
  state: WizardProgressStepState;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-5 items-center justify-center rounded-full text-xs font-medium',
        state === 'complete' && 'bg-state-success text-state-success-foreground',
        state === 'current' && 'bg-state-progress text-state-progress-foreground',
        state === 'incomplete' && 'bg-state-inactive text-state-inactive-foreground',
        className,
      )}
    >
      {state === 'complete' ? <CheckIcon className="size-3" /> : index + 1}
    </span>
  );
}

export function WizardProgress({ className, ...props }: ComponentProps<'button'>) {
  const { steps, currentStepId, focusStep, isStepComplete } = useWizardContext();
  const pendingStepFocus = useRef<string | null>(null);

  if (!steps.length) return null;

  const stepStates = steps.map((step, index) => {
    const isCurrent = step.id === currentStepId;
    return {
      step,
      index,
      isCurrent,
      state: getWizardProgressStepState({
        isComplete: isStepComplete(step.id),
        isCurrent,
      }),
    };
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          aria-label="Open wizard progress menu"
          className={cn(
            'hidden max-w-full justify-self-center overflow-x-auto hover:bg-muted/50 aria-expanded:bg-muted/50 sm:col-span-1 sm:col-start-3 sm:row-start-1 sm:inline-flex',
            className,
          )}
          {...props}
        >
          <span className="pointer-events-none flex items-center justify-center gap-2 text-sm font-medium whitespace-nowrap">
            {stepStates.map(({ step, index, isCurrent, state }) => (
              <span
                key={step.id}
                aria-label={`${step.label} ${state}`}
                aria-current={isCurrent ? 'step' : undefined}
                data-state={state}
                className="flex shrink-0 items-center gap-2"
              >
                {index > 0 ? (
                  <span aria-hidden className="h-0.5 w-4 shrink-0 rounded-full bg-border" />
                ) : null}
                <WizardProgressStepMarker index={index} state={state} />
                <span
                  className={cn('text-sm font-medium text-foreground', !isCurrent && 'sr-only')}
                >
                  {step.label}
                </span>
                <span className="sr-only">{state}</span>
              </span>
            ))}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        onCloseAutoFocus={(event) => {
          // we have to focusStep here as opposed to at the item itself to avoid focus flipping
          // back to the trigger
          const stepId = pendingStepFocus.current;
          if (!stepId) return;
          event.preventDefault();
          pendingStepFocus.current = null;
          focusStep(stepId, 'user');
        }}
      >
        {stepStates.map(({ step, index, isCurrent, state }) => (
          <DropdownMenuItem
            key={step.id}
            aria-label={`${step.label} ${state}`}
            aria-current={isCurrent ? 'step' : undefined}
            data-state={state}
            className="gap-3 px-2 py-1.5 aria-[current]:font-medium"
            onSelect={() => {
              pendingStepFocus.current = step.id;
            }}
          >
            <WizardProgressStepMarker className="shrink-0" index={index} state={state} />
            <span className="min-w-0 truncate">{step.label}</span>
            <span className="sr-only">{state}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
